/**
 * Lecture complète d'une liste paginée — l'unique implémentation du client.
 *
 * Le serveur borne `pageSize` à `LIMITS.maxPageSize` (200) et rend 50 éléments par défaut
 * (`LIMITS.pageSize`) : un appel nu `GET /teams` coupait donc la liste à 50 sans le moindre
 * signal (audit client). `fetchAllPages` demande la taille maximale et boucle sur
 * `meta.pageCount` jusqu'au bout.
 *
 * `MAX_PAGES` est une borne de sécurité, pas un plafond métier : 30 pages de 200 = 6000
 * éléments, bien au-delà de tout parc réel (cf. `LIMITS.maxImportRows`). Si elle est atteinte
 * (serveur bogué, `pageCount` aberrant), la boucle s'arrête et renvoie `truncated: true` pour
 * que l'appelant l'affiche (`TruncatedListWarning`) — jamais de troncature silencieuse.
 */

import { LIMITS } from '@exiplan/shared-types';
import { api } from './api.js';

export const MAX_PAGES = 30;

/**
 * @param {string} path  Chemin d'API, éventuellement avec une query (`/projects/p1/tasks?kind=phase`).
 * @param {{ signal?: AbortSignal, auth?: boolean }} [options]
 * @returns {Promise<{ data: any[], meta: { total: number, page: number, pageSize: number, pageCount: number }, truncated: boolean }>}
 *   `meta` décrit la liste complète (une seule « page »), de sorte qu'un appelant habitué à
 *   `{ data, meta }` n'a rien à changer.
 */
export async function fetchAllPages(path, options = {}) {
  const [base, rawQuery = ''] = path.split('?');
  const pageSize = LIMITS.maxPageSize;
  let items = [];
  let total = 0;
  let truncated = false;

  for (let page = 1; ; page += 1) {
    const params = new URLSearchParams(rawQuery);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const response = await api.get(`${base}?${params.toString()}`, options);
    items = items.concat(response?.data ?? []);
    total = response?.meta?.total ?? items.length;
    const pageCount = response?.meta?.pageCount ?? 1;
    if (page >= pageCount) break;
    if (page >= MAX_PAGES) {
      truncated = true;
      break;
    }
  }

  return {
    data: items,
    meta: { total, page: 1, pageSize: Math.max(items.length, 1), pageCount: 1 },
    truncated,
  };
}
